/**
 * smooth-car-sprite.ts — sprite sheet plumbing for SmoothCarMarker
 * (PLA-262 / PLA-265 / PLA-267 / PLA-278 / PLA-292 / PLA-294).
 *
 * The marker renders one of two bundled pseudo-3D car sprites (64 heading-
 * indexed frames, frame 0 = north, clockwise) instead of a remote PNG: the
 * HDP-liveried transporter ("hdp") or the customer's own car ("customer").
 * Both sheets are schema-identical (PLA-277): 64 frames, 8 columns. The active
 * variant is chosen per placement (PLA-278) -- see `carSpriteVariant`. Everything
 * in this module is React-free: sheet/density resolution, frame geometry, the
 * regular-marker fallback icon, the docking-settle keyframes, and the bearing
 * math used to derive heading from consecutive interpolated positions.
 *
 * PLA-292 adds:
 *  - burstConfetti  (one-shot completion celebration)
 *
 * PLA-294 replaces the PLA-292 flat 2D scanner (buildScanOverlay, now removed)
 * with a Full-Uber 3D layered design:
 *  - buildScanGroundLayer()  soft radial contact shadow that grounds the car
 *  - buildScanBeamLayer()    masked sweep beam clipped to the car silhouette
 *  - buildScanStaticSheen()  reduced-motion static variant
 *
 * PLA-294 follow-up (live feedback): the red sonar ground-pulse rings were
 * removed -- the car is parked AT the hub and the hub endpoint marker already
 * renders its own rings, so a second set on the car was redundant. The contact
 * shadow was reworked from a solid-fill+blur ellipse (which kept a boxy edge
 * anchored to the square sprite frame) into a radial-gradient ellipse that
 * feathers to fully transparent before its own edges -- no square artifact.
 */
import { Asset } from "expo-asset"

import { colors } from "@/theme"

/**
 * Which bundled car sheet a marker renders (PLA-278). "hdp" is the HDP-liveried
 * transporter (driver outbound); "customer" is the customer's own car (every
 * other on-map car: return leg, at-hub inspection, pickup placeholder).
 */
export type SpriteVariant = "hdp" | "customer"

interface SheetSet {
  meta: { frameCount: number; columns: number }
  /**
   * "-1x"/"-2x" (not "@1x"/"@2x"): Metro parses an "@<n>x" filename suffix as a
   * density qualifier and refuses to resolve it as a literal require() path.
   * The require() paths MUST stay static string literals so Metro can bundle them.
   */
  sheet1x: number
  sheet2x: number
}

/**
 * Static, variant-keyed sheet registry. Both metas are frameCount 64 / 8 cols
 * (PLA-277), so the geometry exports below derive from either -- we use the HDP
 * sheet as the canonical source. require() calls are static literals (Metro).
 */
const SHEETS: Record<SpriteVariant, SheetSet> = {
  hdp: {
    meta: require("@assets/sprites/hdp-car.json") as { frameCount: number; columns: number },
    sheet1x: require("@assets/sprites/hdp-car-1x.png"),
    sheet2x: require("@assets/sprites/hdp-car-2x.png"),
  },
  customer: {
    meta: require("@assets/sprites/customer-car.json") as {
      frameCount: number
      columns: number
    },
    sheet1x: require("@assets/sprites/customer-car-1x.png"),
    sheet2x: require("@assets/sprites/customer-car-2x.png"),
  },
}

/** Display size of the marker in CSS px (square; keeps the old ~60px footprint). */
export const SPRITE_DISPLAY_SIZE = 60
// Both variants share frame geometry (PLA-277); derive from the HDP sheet.
export const SHEET_COLUMNS = SHEETS.hdp.meta.columns
export const SHEET_ROWS = Math.ceil(SHEETS.hdp.meta.frameCount / SHEETS.hdp.meta.columns)
/** Opacity crossfade duration between adjacent sprite frames. */
export const CROSSFADE_MS = 120

/** Resolves the bundled sheet URL for the given variant + current screen density. */
export function spriteSheetUri(variant: SpriteVariant = "hdp"): string {
  const dpr = typeof window !== "undefined" && window.devicePixelRatio ? window.devicePixelRatio : 1
  const set = SHEETS[variant]
  return Asset.fromModule(dpr > 1.25 ? set.sheet2x : set.sheet1x).uri
}

const preloadedVariants = new Set<SpriteVariant>()

/**
 * Warms both density sheets of `variant` once so the first marker render (or a
 * mid-leg variant swap) never flashes empty (PLA-278). Call for every variant
 * that may appear so swapping legs is instant.
 */
export function preloadSpriteSheets(variant: SpriteVariant = "hdp"): void {
  if (
    preloadedVariants.has(variant) ||
    typeof window === "undefined" ||
    typeof window.Image !== "function"
  ) {
    return
  }
  preloadedVariants.add(variant)
  const set = SHEETS[variant]
  for (const sheet of [set.sheet1x, set.sheet2x]) {
    const img = new window.Image()
    img.src = Asset.fromModule(sheet).uri
  }
}

// ---------------------------------------------------------------------------
// Arrival docking settle (PLA-262 / PLA-267)
// ---------------------------------------------------------------------------

/** Total length of the one-shot docking settle. Must stay < 800ms. */
export const DOCK_SETTLE_MS = 700

/** Injects the docking keyframes once. translateZ(0) preserves the GPU layer
 * the marker container pins via its inline transform. */
export function ensureDockKeyframes(): void {
  if (typeof document === "undefined" || document.getElementById("hdp-car-dock-styles")) {
    return
  }
  const style = document.createElement("style")
  style.id = "hdp-car-dock-styles"
  style.textContent = `
    @keyframes hdpCarDockSettle {
      0% { transform: translateZ(0) scale(1); }
      45% { transform: translateZ(0) scale(1.06); }
      100% { transform: translateZ(0) scale(1); }
    }
  `
  document.head.appendChild(style)
}

/** CSS background-position selecting `frameIndex` out of the packed sheet. */
export function frameBackgroundPosition(frameIndex: number): string {
  const col = frameIndex % SHEET_COLUMNS
  const row = Math.floor(frameIndex / SHEET_COLUMNS)
  return `${-col * SPRITE_DISPLAY_SIZE}px ${-row * SPRITE_DISPLAY_SIZE}px`
}

/** DOM handles for the AdvancedMarkerElement car content (PLA-272 / PLA-274). */
export interface SpriteContainerBuild {
  /** 1x1 anchor div handed to AdvancedMarkerElement as `content` (PLA-274). */
  wrapper: HTMLDivElement
  /** 60x60 sprite container, absolutely centered on the wrapper. */
  container: HTMLDivElement
  /** Two stacked sprite layers; the back one exists for the crossfade. */
  layers: [HTMLDivElement, HTMLDivElement]
}

/**
 * Builds the AdvancedMarkerElement content: a GPU-pinned container holding
 * two stacked sprite layers sharing the bundled sheet (layer 0 starts
 * visible; layer 1 backs the opacity crossfade between adjacent frames).
 * `initialOpacity` lets a marker created mid-staleness start dimmed (PLA-272);
 * the dim targets the container (like the staleness flip writer), never the
 * wrapper.
 *
 * Anchoring (PLA-274): AdvancedMarkerElement anchors the bottom-center of its
 * content at the lat/lng; the 1x1 wrapper makes that (~)the GPS point, and
 * the absolutely-positioned sprite container below centers on it at every
 * zoom level (same idiom as AnimatedPickupMarker). Centering lives in
 * left/top -- NOT transform -- because the dock-settle keyframes
 * (hdpCarDockSettle) overwrite the container's animation/transform.
 */
export function buildSpriteContainer(
  initialFrameIndex: number,
  initialOpacity: string,
  variant: SpriteVariant = "hdp",
): SpriteContainerBuild {
  const wrapper = document.createElement("div")
  wrapper.style.position = "relative"
  wrapper.style.width = "1px"
  wrapper.style.height = "1px"

  const container = document.createElement("div")
  container.className = "smooth-car-marker"
  container.style.width = `${SPRITE_DISPLAY_SIZE}px`
  container.style.height = `${SPRITE_DISPLAY_SIZE}px`
  container.style.position = "absolute"
  container.style.left = `-${SPRITE_DISPLAY_SIZE / 2}px`
  container.style.top = `-${SPRITE_DISPLAY_SIZE / 2}px`
  container.style.transition = "none" // Disable CSS transitions
  container.style.willChange = "transform"
  container.style.backfaceVisibility = "hidden"
  container.style.transform = "translateZ(0)" // Force GPU acceleration
  container.style.opacity = initialOpacity

  const sheetUri = spriteSheetUri(variant)
  const initialPosition = frameBackgroundPosition(initialFrameIndex)
  const layerA = document.createElement("div")
  const layerB = document.createElement("div")
  ;[layerA, layerB].forEach((layer, index) => {
    layer.style.position = "absolute"
    layer.style.top = "0"
    layer.style.left = "0"
    layer.style.width = "100%"
    layer.style.height = "100%"
    layer.style.backgroundImage = `url(${sheetUri})`
    layer.style.backgroundRepeat = "no-repeat"
    layer.style.backgroundSize = `${SPRITE_DISPLAY_SIZE * SHEET_COLUMNS}px ${
      SPRITE_DISPLAY_SIZE * SHEET_ROWS
    }px`
    layer.style.backgroundPosition = initialPosition
    layer.style.opacity = index === 0 ? "1" : "0"
    layer.style.willChange = "opacity"
    container.appendChild(layer)
  })

  wrapper.appendChild(container)
  return { wrapper, container, layers: [layerA, layerB] }
}

/**
 * google.maps.Icon for the regular-marker fallback (no AdvancedMarkerElement):
 * the sheet is scaled to display size and the frame selected via sprite
 * `origin`/`size` -- no crossfade possible there, plain frame swaps only.
 */
export function fallbackIconForFrame(
  frameIndex: number,
  variant: SpriteVariant = "hdp",
): google.maps.Icon {
  const col = frameIndex % SHEET_COLUMNS
  const row = Math.floor(frameIndex / SHEET_COLUMNS)
  return {
    url: spriteSheetUri(variant),
    size: new google.maps.Size(SPRITE_DISPLAY_SIZE, SPRITE_DISPLAY_SIZE),
    scaledSize: new google.maps.Size(
      SPRITE_DISPLAY_SIZE * SHEET_COLUMNS,
      SPRITE_DISPLAY_SIZE * SHEET_ROWS,
    ),
    origin: new google.maps.Point(col * SPRITE_DISPLAY_SIZE, row * SPRITE_DISPLAY_SIZE),
    anchor: new google.maps.Point(SPRITE_DISPLAY_SIZE / 2, SPRITE_DISPLAY_SIZE / 2),
  }
}

// ---------------------------------------------------------------------------
// PLA-294: At-hub inspection scanner -- Full Uber 3D layered design
// ---------------------------------------------------------------------------
// All magic numbers are named constants so live-tuning is one-liners.

/** Brand red used for the scanner; matches colors.primary (#FF382F). */
export const SCANNER_RED = "#FF382F"

// --- Beam layer tunables ---
/** Height of the sweep beam in px (leading edge + trailing gradient). */
const BEAM_HEIGHT_PX = 20
/** Bright leading-edge stripe height in px at the top of the beam. */
const BEAM_EDGE_HEIGHT_PX = 3
/** Bloom blur on the sweep bar in px (stays masked to the car silhouette). */
const BEAM_BLOOM_BLUR_PX = 6
/** Full sweep duration in ms (top -> bottom of the SPRITE_DISPLAY_SIZE box). */
const BEAM_SWEEP_MS = 2000
/** Subtle red sheen opacity on the car body while scanning (0 = off). */
const BEAM_SHEEN_OPACITY = 0.12

// --- Ground (contact shadow) tunables ---
// A single soft contact shadow grounds the car. It is a radial-gradient ellipse
// (transparent before its own edges) so it can never show a hard/boxy boundary,
// and it is sized + positioned to hug the car footprint, not the square frame.
/** Contact shadow ellipse width in px. */
const SHADOW_W_PX = 34
/** Contact shadow ellipse height in px (flattened for perspective). */
const SHADOW_H_PX = 12
/** Extra blur on the radial shadow in px for additional softness (0 = off). */
const SHADOW_BLUR_PX = 3
/** Peak shadow alpha at the gradient center (feathers to 0 at the edge). */
const SHADOW_OPACITY = 0.4
/** Fraction of SHADOW_OPACITY at the gradient mid-stop (controls falloff shape). */
const SHADOW_MID_ALPHA_RATIO = 0.55
/** Radial-gradient stop (%) where the mid alpha sits before fading to transparent. */
const SHADOW_MID_STOP_PCT = 50
/** Radial-gradient stop (%) where the shadow reaches full transparency. */
const SHADOW_EDGE_STOP_PCT = 80
/** Vertical position of the shadow center as a fraction of SPRITE_DISPLAY_SIZE. */
const SHADOW_VERTICAL_POSITION_RATIO = 0.62
/** Beam leading-edge bright stripe color. */
const BEAM_EDGE_COLOR = "rgba(255,255,255,0.9)"
/** Beam gradient: red alpha for the bright transition zone (hex suffix). */
const BEAM_GRADIENT_RED_ALPHA_BRIGHT = "cc"
/** Beam gradient: red alpha for the dim trailing zone (hex suffix). */
const BEAM_GRADIENT_RED_ALPHA_DIM = "44"
/** Multiplier applied to BEAM_SHEEN_OPACITY for the static reduced-motion sheen.
 *  Static state needs more visibility than animated motion (no sweep cue). */
const STATIC_SHEEN_OPACITY_MULTIPLIER = 2

/**
 * Injects the scan animation keyframes once (mirrors ensureDockKeyframes).
 * Only the masked sweep beam animates now:
 *  - hdpCarScanBeam: sweep bar translates top->bottom across the sprite box.
 * (PLA-294 follow-up removed hdpCarGroundPulse along with the ground rings.)
 */
export function ensureScanKeyframes(): void {
  if (typeof document === "undefined" || document.getElementById("hdp-car-scan-styles")) {
    return
  }
  const style = document.createElement("style")
  style.id = "hdp-car-scan-styles"
  style.textContent = `
    @keyframes hdpCarScanBeam {
      0%   { transform: translateY(-${BEAM_HEIGHT_PX}px); }
      100% { transform: translateY(${SPRITE_DISPLAY_SIZE}px); }
    }
  `
  document.head.appendChild(style)
}

/**
 * Builds the ground layer: a single soft contact shadow that grounds the car.
 * Caller inserts this BEFORE the sprite layers (insertBefore(content.firstChild))
 * so it sits underneath the car. Never touches the container transform.
 *
 * The shadow is a radial-gradient ellipse that fades to fully transparent before
 * the element's own edges, so it feathers naturally with no hard/boxy boundary
 * and hugs the car footprint -- it never reveals the square sprite frame. The
 * red sonar ground-pulse rings were removed (the hub endpoint marker already
 * shows rings; a second set on the parked car was redundant).
 */
export function buildScanGroundLayer(): HTMLDivElement {
  const ground = document.createElement("div")
  ground.style.position = "absolute"
  ground.style.top = "0"
  ground.style.left = "0"
  ground.style.width = "100%"
  ground.style.height = "100%"
  ground.style.pointerEvents = "none"

  // Contact shadow: a radial-gradient ellipse (transparent at the edges) so it
  // feathers with no hard boundary. left-centered, vertically under the car.
  const inner = `rgba(0,0,0,${SHADOW_OPACITY})`
  const mid = `rgba(0,0,0,${SHADOW_OPACITY * SHADOW_MID_ALPHA_RATIO})`
  const shadow = document.createElement("div")
  shadow.style.position = "absolute"
  shadow.style.width = `${SHADOW_W_PX}px`
  shadow.style.height = `${SHADOW_H_PX}px`
  shadow.style.left = `${(SPRITE_DISPLAY_SIZE - SHADOW_W_PX) / 2}px`
  shadow.style.top = `${SPRITE_DISPLAY_SIZE * SHADOW_VERTICAL_POSITION_RATIO}px`
  shadow.style.background = `radial-gradient(ellipse at center, ${inner} 0%, ${mid} ${SHADOW_MID_STOP_PCT}%, rgba(0,0,0,0) ${SHADOW_EDGE_STOP_PCT}%)`
  if (SHADOW_BLUR_PX > 0) {
    shadow.style.filter = `blur(${SHADOW_BLUR_PX}px)`
  }
  ground.appendChild(shadow)

  return ground
}

/**
 * Builds the beam layer: a masked sweep bar with a bright leading edge +
 * soft trailing gradient, a faint bloom, and a subtle scan sheen.
 * All elements share the same CSS mask so the beam is clipped to the car's
 * alpha silhouette -- it never lights up transparent corners.
 *
 * Both -webkit-mask-* and standard mask-* properties are set for
 * Safari and Chrome compatibility.
 *
 * @param maskUri      result of spriteSheetUri(variant)
 * @param maskPosition result of frameBackgroundPosition(frameIndex)
 */
export function buildScanBeamLayer(maskUri: string, maskPosition: string): HTMLDivElement {
  const maskSize = `${SPRITE_DISPLAY_SIZE * SHEET_COLUMNS}px ${SPRITE_DISPLAY_SIZE * SHEET_ROWS}px`

  const beam = document.createElement("div")
  beam.style.position = "absolute"
  beam.style.top = "0"
  beam.style.left = "0"
  beam.style.width = "100%"
  beam.style.height = "100%"
  beam.style.pointerEvents = "none"
  // Clip beam to the car silhouette via the sprite sheet as a CSS mask.
  beam.style.webkitMaskImage = `url(${maskUri})`
  beam.style.maskImage = `url(${maskUri})`
  beam.style.webkitMaskSize = maskSize
  beam.style.maskSize = maskSize
  beam.style.webkitMaskPosition = maskPosition
  beam.style.maskPosition = maskPosition
  beam.style.webkitMaskRepeat = "no-repeat"
  beam.style.maskRepeat = "no-repeat"

  // Sweep bar: bright leading edge + trailing red gradient, translates Y.
  // Bar itself is NOT blurred -- a dedicated bloom child handles the bloom effect
  // so only the leading edge softens, not the entire beam.
  const bar = document.createElement("div")
  bar.style.position = "absolute"
  bar.style.left = "0"
  bar.style.width = "100%"
  bar.style.height = `${BEAM_HEIGHT_PX}px`
  bar.style.background = `linear-gradient(
    to bottom,
    ${BEAM_EDGE_COLOR} 0px,
    ${BEAM_EDGE_COLOR} ${BEAM_EDGE_HEIGHT_PX}px,
    ${SCANNER_RED}${BEAM_GRADIENT_RED_ALPHA_BRIGHT} ${BEAM_EDGE_HEIGHT_PX + 2}px,
    ${SCANNER_RED}${BEAM_GRADIENT_RED_ALPHA_DIM} ${BEAM_HEIGHT_PX - 4}px,
    transparent ${BEAM_HEIGHT_PX}px
  )`
  bar.style.animation = `hdpCarScanBeam ${BEAM_SWEEP_MS}ms linear infinite`
  beam.appendChild(bar)

  // Dedicated bloom child: soft blurred overlay at the leading edge only,
  // creating a "light above surface" cue without blurring the whole beam.
  const bloom = document.createElement("div")
  bloom.style.position = "absolute"
  bloom.style.left = "0"
  bloom.style.top = "0"
  bloom.style.width = "100%"
  bloom.style.height = `${BEAM_EDGE_HEIGHT_PX + 4}px`
  bloom.style.backgroundColor = BEAM_EDGE_COLOR
  bloom.style.filter = `blur(${BEAM_BLOOM_BLUR_PX}px)`
  bloom.style.pointerEvents = "none"
  bar.appendChild(bloom)

  // Subtle scan sheen: low-opacity red wash that reads as "being scanned".
  if (BEAM_SHEEN_OPACITY > 0) {
    const sheen = document.createElement("div")
    sheen.style.position = "absolute"
    sheen.style.top = "0"
    sheen.style.left = "0"
    sheen.style.width = "100%"
    sheen.style.height = "100%"
    sheen.style.backgroundColor = SCANNER_RED
    sheen.style.opacity = String(BEAM_SHEEN_OPACITY)
    beam.appendChild(sheen)
  }

  return beam
}

/**
 * Builds a static beam layer for reduced-motion mode: no sweep animation,
 * just a masked red sheen so the scanning state is still visually indicated.
 *
 * @param maskUri      result of spriteSheetUri(variant)
 * @param maskPosition result of frameBackgroundPosition(frameIndex)
 */
export function buildScanStaticSheen(maskUri: string, maskPosition: string): HTMLDivElement {
  const maskSize = `${SPRITE_DISPLAY_SIZE * SHEET_COLUMNS}px ${SPRITE_DISPLAY_SIZE * SHEET_ROWS}px`

  const staticSheen = document.createElement("div")
  staticSheen.style.position = "absolute"
  staticSheen.style.top = "0"
  staticSheen.style.left = "0"
  staticSheen.style.width = "100%"
  staticSheen.style.height = "100%"
  staticSheen.style.pointerEvents = "none"
  // Slightly more visible than animated sheen since there is no sweep cue.
  staticSheen.style.backgroundColor = SCANNER_RED
  staticSheen.style.opacity = String(BEAM_SHEEN_OPACITY * STATIC_SHEEN_OPACITY_MULTIPLIER)
  // Apply sprite mask so sheen is also clipped to the car silhouette.
  staticSheen.style.webkitMaskImage = `url(${maskUri})`
  staticSheen.style.maskImage = `url(${maskUri})`
  staticSheen.style.webkitMaskSize = maskSize
  staticSheen.style.maskSize = maskSize
  staticSheen.style.webkitMaskPosition = maskPosition
  staticSheen.style.maskPosition = maskPosition
  staticSheen.style.webkitMaskRepeat = "no-repeat"
  staticSheen.style.maskRepeat = "no-repeat"
  return staticSheen
}

// ---------------------------------------------------------------------------
// PLA-292: One-shot completion confetti burst (Web Animations API)
// ---------------------------------------------------------------------------

/** Non-red confetti palette; mirrors CelebrationBurst.tsx colorForParticle(). */
const NON_RED_CONFETTI = [
  colors.success,
  colors.confettiYellow,
  colors.confettiPink,
  colors.confettiPurple,
  colors.confettiCyan,
]

/** Particle 0 = brand red; 1..n cycle the non-red set (one-red rule). */
const confettiColorForParticle = (i: number): string =>
  i === 0 ? colors.primary : NON_RED_CONFETTI[(i - 1) % NON_RED_CONFETTI.length]

/**
 * Emits ~10 confetti particles from `host` using the Web Animations API.
 * Each particle is 8x8 px, absolutely positioned, pointerEvents none, and
 * removes itself from the DOM on finish -- no leaks.
 *
 * Append to the 1x1 wrapper (overflow visible) so particles radiate from
 * the car centre and are NOT clipped by the 60x60 sprite container.
 */
export function burstConfetti(host: HTMLElement): void {
  const PARTICLE_COUNT = 10
  const MIN_DIST = 50
  const MAX_DIST = 90

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const angle = (i / PARTICLE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.6
    const dist = MIN_DIST + Math.random() * (MAX_DIST - MIN_DIST)
    const x = Math.cos(angle) * dist
    const y = Math.sin(angle) * dist

    const particle = document.createElement("div")
    particle.style.position = "absolute"
    particle.style.width = "8px"
    particle.style.height = "8px"
    particle.style.borderRadius = "4px"
    particle.style.backgroundColor = confettiColorForParticle(i)
    particle.style.pointerEvents = "none"
    // Center on the 1x1 wrapper anchor.
    particle.style.top = "-4px"
    particle.style.left = "-4px"

    host.appendChild(particle)

    const anim = particle.animate(
      [
        { transform: "translate(0, 0) scale(1)", opacity: "1" },
        { transform: `translate(${x}px, ${y}px) scale(0.4)`, opacity: "0" },
      ],
      { duration: 800, easing: "ease-out", fill: "forwards" },
    )

    anim.onfinish = () => {
      particle.remove()
    }
  }
}

/** Returns true when lat/lng are finite numbers safe to pass to Google Maps. */
export function areCoordsValid(lat: unknown, lng: unknown): lat is number {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    !isNaN(lat as number) &&
    !isNaN(lng as number)
  )
}

/** Compass bearing in degrees (0 = north, clockwise) between two points. */
export function calculateHeading(
  from: { lat: number; lng: number },
  to: { lat: number; lng: number },
): number {
  const dLng = ((to.lng - from.lng) * Math.PI) / 180
  const lat1 = (from.lat * Math.PI) / 180
  const lat2 = (to.lat * Math.PI) / 180

  const y = Math.sin(dLng) * Math.cos(lat2)
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)

  let heading = (Math.atan2(y, x) * 180) / Math.PI
  // Normalize to 0-360 degrees
  heading = (heading + 360) % 360
  return heading
}
